import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Calendar, TrendingDown, Eye, Upload, Loader2, Trash2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/Navigation';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL || '';

const OptimizationReportsListPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleDeleteReport = async (shareToken, clientName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le rapport de ${clientName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/optimization-reports/${shareToken}`, {
        headers: getAuthHeader()
      });
      
      // Recharger la liste après suppression
      loadReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Erreur lors de la suppression du rapport');
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/optimization-reports`, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        setReports(response.data.reports);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
        <Navigation />
        <div className="container mx-auto px-4 py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
        <Navigation />
        <div className="container mx-auto px-4 py-12">
          <p className="text-destructive text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
      <Navigation />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold gradient-text mb-2">
                Rapports d'Optimisation
              </h1>
              <p className="text-muted-foreground">
                {reports.length} rapport{reports.length !== 1 ? 's' : ''} transformé{reports.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <Link to="/optimization/upload">
              <Button size="lg" className="gap-2">
                <Upload className="w-4 h-4" />
                Nouveau rapport
              </Button>
            </Link>
          </div>

          {reports.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">Aucun rapport disponible</h3>
                  <p className="text-muted-foreground mb-6">
                    Commencez par charger votre premier rapport d'optimisation
                  </p>
                  <Link to="/optimization/upload">
                    <Button>
                      <Upload className="w-4 h-4 mr-2" />
                      Charger un rapport
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {reports.map((report) => (
                <OptimizationReportCard 
                  key={report._id} 
                  report={report} 
                  onDelete={handleDeleteReport}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OptimizationReportCard = ({ report, onDelete }) => {
  const uploadDate = new Date(report.created_at || report.upload_date || Date.now());
  
  const handleViewReport = () => {
    sessionStorage.setItem('from_reports_list', 'true');
  };

  const handleShare = () => {
    const shareLink = `${window.location.origin}/optimization/report/${report.share_token}`;
    navigator.clipboard.writeText(shareLink);
    toast.success('Lien copié !', {
      description: 'Le lien du rapport a été copié dans le presse-papiers',
    });
  };

  return (
    <Card className="card-hover transition-all duration-300 hover:-translate-y-1">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-2xl mb-2">{report.client_name}</CardTitle>
            <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                PDL: {report.pdl}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {format(uploadDate, 'dd MMMM yyyy', { locale: fr })}
              </span>
            </div>
          </div>
          
          <Badge variant="outline" className="bg-success/10 text-success border-success/20">
            Optimisation
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-accent/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Coût Actuel</p>
            <p className="text-2xl font-bold">
              {report.current_costs?.total?.toFixed(2).replace('.', ',')} €
            </p>
          </div>
          
          <div className="p-4 bg-accent/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Coût Optimisé</p>
            <p className="text-2xl font-bold text-primary">
              {report.optimized_costs?.total?.toFixed(2).replace('.', ',')} €
            </p>
          </div>
          
          <div className="p-4 bg-success/10 rounded-lg">
            <p className="text-sm text-success mb-1 flex items-center gap-1">
              <TrendingDown className="w-4 h-4" />
              Économie Annuelle
            </p>
            <p className="text-2xl font-bold text-success">
              {report.savings?.total ? `${report.savings.total.toFixed(2).replace('.', ',')} €` : 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 flex-col sm:flex-row">
          <Link to={`/optimization/report/${report.share_token}`} onClick={handleViewReport} className="flex-1">
            <Button className="w-full" size="lg">
              <Eye className="w-4 h-4 mr-2" />
              Voir le rapport
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="lg"
            onClick={handleShare}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Partager
          </Button>
          <Button 
            variant="destructive" 
            size="lg"
            onClick={() => onDelete(report.share_token, report.client_name)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OptimizationReportsListPage;
